/**
 * InlineSparkline — generates an inline SVG string for embedding in
 * dangerouslySetInnerHTML content.  Renders a tiny area-chart sparkline
 * (gradient fill + polyline) with optional highlight dots for specified GEOIDs.
 *
 * Usage in Stories Content CSV:
 *   [spark=0648844]          — single highlight dot
 *   [spark=0648844,0631414]  — multiple highlight dots
 *
 * Call processSparklineTags() to extract tags, build SVGs, and inject them
 * back into the HTML string in one pass.
 */

import Colors from '../utilities/Colors';

// ─── Core SVG builder ──────────────────────────────────────────────

export interface SparklineOptions {
  /** Sorted data array — each item has { id: string, value: number|string } */
  sortedArray: any[];
  /** Array of GEO_IDs to highlight with dots */
  geoids: string[];
  /** Color class name from ORDER CSV (e.g. "green", "red") */
  colorClass: string;
  /** Unique id prefix to avoid SVG gradient collisions */
  idPrefix: string;
  /** Width in px (default 60) */
  width?: number;
  /** Height in px (default 12) */
  height?: number;
}

export function buildSparklineSvg(opts: SparklineOptions): string {
  const {
    sortedArray,
    geoids,
    colorClass,
    idPrefix,
    width: sparkW = 60,
    height: sparkH = 12,
  } = opts;

  const padX = 3;
  const padY = 3;

  // Classify each item as numeric or NA
  const allItems = sortedArray.map((item: any) => {
    const isNumeric = typeof item.value === 'number' && !isNaN(item.value);
    return { id: item.id, value: item.value, isNumeric };
  });

  const numericItems = allItems.filter((d: any) => d.isNumeric);
  if (numericItems.length <= 1) return '';

  const minVal = numericItems[0].value;
  const maxVal = numericItems[numericItems.length - 1].value;
  const range = maxVal - minVal || 1;
  const n = allItems.length; // total count including NAs

  const lineColor = Colors.getColorByClass(colorClass, 2);
  const darkColor = Colors.getColorByClass(colorClass, 0);
  const grayColor = Colors.moaNA;

  // Build coordinate arrays — NAs sit at the baseline
  const geoidSet = new Set(geoids.map((g) => g.trim()));
  const highlights: { x: number; y: number }[] = [];

  // // Quintile dot color (disabled — reverted to uniform darkColor)
  // const naCount = allItems.length - numericItems.length;

  // Separate into segments: runs of numeric vs NA items
  const points: { x: number; y: number; isNA: boolean }[] = allItems.map(
    (item: any, i: number) => {
      const x = padX + (i / (n - 1)) * (sparkW - 2 * padX);
      const y = item.isNumeric
        ? padY + (1 - (item.value - minVal) / range) * (sparkH - 2 * padY)
        : sparkH - padY; // NA → baseline
      if (geoidSet.has(item.id?.toString())) {
        // // Quintile color (disabled — reverted to uniform darkColor)
        // const dotColor = Colors.getColorQuintile(i, naCount, n, colorClass);
        // highlights.push({ x, y, color: dotColor });
        highlights.push({ x, y });
      }
      return { x, y, isNA: !item.isNumeric };
    }
  );

  // Build segmented polylines: consecutive runs of same type
  // Each segment overlaps by 1 point with neighbors for seamless joins
  const segments: { pts: string[]; isNA: boolean }[] = [];
  let curNA = points[0].isNA;
  let curPts = [`${points[0].x},${points[0].y}`];

  for (let i = 1; i < points.length; i++) {
    if (points[i].isNA !== curNA) {
      // Close current segment, start new one with overlap point
      segments.push({ pts: [...curPts], isNA: curNA });
      curNA = points[i].isNA;
      curPts = [`${points[i - 1].x},${points[i - 1].y}`, `${points[i].x},${points[i].y}`];
    } else {
      curPts.push(`${points[i].x},${points[i].y}`);
    }
  }
  segments.push({ pts: curPts, isNA: curNA });

  // Build full path for the fill polygon (all points, colored gradient)
  const allLinePoints = points.map((p) => `${p.x},${p.y}`);
  const fullLinePath = allLinePoints.join(' ');
  const areaPath = `${fullLinePath} ${sparkW - padX},${sparkH - padY} ${padX},${sparkH - padY}`;

  const gradientId = `${idPrefix}-grad`;

  // Colored fill covers everything; segmented strokes differentiate NA vs numeric
  const polylines = segments
    .map(
      (seg) =>
        `<polyline points="${seg.pts.join(' ')}" fill="none" ` +
        `stroke="${seg.isNA ? grayColor : lineColor}" ` +
        `stroke-width=${seg.isNA ? 0.25 : 1} stroke-linejoin="round" stroke-linecap="round"/>`
    )
    .join('');

  const dots = highlights
    .map(
      (h) =>
        `<circle cx="${h.x}" cy="${h.y}" r="2.5" fill="${darkColor}" stroke="#fff" stroke-width="0.5"/>`
    )
    .join('');

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${sparkW}" height="${sparkH}" ` +
    `style="display:inline;vertical-align:middle;margin:0 2px;overflow:visible;">` +
    `<defs><linearGradient id="${gradientId}" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0%" stop-color="${lineColor}" stop-opacity="0.35"/>` +
    `<stop offset="100%" stop-color="${lineColor}" stop-opacity="0.05"/>` +
    `</linearGradient></defs>` +
    `<polygon points="${areaPath}" fill="url(#${gradientId})"/>` +
    polylines +
    dots +
    `</svg>`
  );
}

// ─── Tag processor ─────────────────────────────────────────────────

/**
 * Extracts [spark=GEOID] or [spark=GEOID1,GEOID2,...] tags from raw text,
 * replaces them with placeholder tokens, then after markup parsing replaces
 * placeholders with inline SVG strings.
 *
 * @param rawText    - The Stories Content string (before markup parsing)
 * @param parseMarkup - The custom markup parser function (text → HTML)
 * @param sortedArray - data.dataArray[variableName]
 * @param colorClass  - Module color class
 * @param variableName - Variable name (used for unique SVG ids)
 * @returns Final HTML string with sparkline SVGs embedded inline
 */
export function processSparklineTags(
  rawText: string,
  parseMarkup: (input: string) => string,
  sortedArray: any[] | undefined,
  colorClass: string,
  variableName: string
): string {
  if (!rawText) return parseMarkup('');

  // 1. Extract [spark=...] tags into placeholders
  const placeholders: { token: string; geoids: string[] }[] = [];
  const preprocessed = rawText.replace(
    /\[spark=([^\]]+)\]/gi,
    (_match: string, content: string) => {
      const token = `__SPARK_${placeholders.length}__`;
      placeholders.push({ token, geoids: content.split(',') });
      return token;
    }
  );

  // 2. Parse the markup (placeholders pass through untouched)
  let html = parseMarkup(preprocessed);

  // 3. Replace placeholders with SVG strings (or remove if no data)
  placeholders.forEach(({ token, geoids }, idx) => {
    if (sortedArray && sortedArray.length > 1) {
      const svg = buildSparklineSvg({
        sortedArray,
        geoids,
        colorClass,
        idPrefix: `spark-${variableName}-${idx}`,
      });
      html = html.replace(token, svg);
    } else {
      html = html.replace(token, '');
    }
  });

  return html;
}
